import React, { useEffect, useState } from 'react';
import './styles.scss';

interface Extras {
  health?: number;
  enchantmentId?: number;
  maxCharge?: number;
  chargePercent?: number;
  name?: string;
  soul?: number;
  poisonId?: number;
  poisonCount?: number;
  removeEnchantmentOnUnequip?: boolean;
}

interface UiItem {
  baseId: number;
  count: number;
  name: string;
  extras?: Extras;
  // Client-computed by tradeService.ts, for list keys only - never sent back
  // as an identifier (see tradeService.ts's own UiItem comment).
  key: string;
}

interface TradeStateDetail {
  partnerName: string;
  inventory: UiItem[];
  myOffer: UiItem[];
  theirOffer: UiItem[];
  myLocked: boolean;
  theirLocked: boolean;
  bothLocked: boolean;
  iAccepted: boolean;
  theyAccepted: boolean;
}

const STACK_PROMPT_THRESHOLD = 1;

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('trade sendMessage', payload);
  }
};

interface ItemListProps {
  items: UiItem[];
  emptyText: string;
  onItemClick?: (item: UiItem) => void;
}

const ItemList = ({ items, emptyText, onItemClick }: ItemListProps) => {
  if (!items || items.length === 0) {
    return <div className="trade-empty">{emptyText}</div>;
  }
  return (
    <div className="trade-list">
      {items.map((item) => (
        <div
          key={item.key}
          className={'trade-item' + (onItemClick ? ' trade-item--clickable' : '')}
          onClick={onItemClick ? () => onItemClick(item) : undefined}
        >
          <span className="trade-item__name">{item.name}</span>
          {item.count > 1 ? <span className="trade-item__count">×{item.count}</span> : null}
        </div>
      ))}
    </div>
  );
};

interface CountPrompt {
  dir: 'add' | 'remove';
  item: UiItem;
}

const Trade = () => {
  const [state, setState] = useState<TradeStateDetail | null>(null);
  const [inviteFrom, setInviteFrom] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<CountPrompt | null>(null);
  const [promptCount, setPromptCount] = useState(1);

  useEffect(() => {
    const showTradeHandler = (e: Event) => {
      setState((e as CustomEvent).detail as TradeStateDetail);
      setInviteFrom(null);
    };
    const hideTradeHandler = () => {
      setState(null);
      setPrompt(null);
    };
    const showInviteHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { fromName: string };
      setInviteFrom(detail.fromName);
    };
    const hideInviteHandler = () => setInviteFrom(null);

    window.addEventListener('skymp5-client:showTradeMenu', showTradeHandler);
    window.addEventListener('skymp5-client:hideTradeMenu', hideTradeHandler);
    window.addEventListener('skymp5-client:showTradeInvite', showInviteHandler);
    window.addEventListener('skymp5-client:hideTradeInvite', hideInviteHandler);
    return () => {
      window.removeEventListener('skymp5-client:showTradeMenu', showTradeHandler);
      window.removeEventListener('skymp5-client:hideTradeMenu', hideTradeHandler);
      window.removeEventListener('skymp5-client:showTradeInvite', showInviteHandler);
      window.removeEventListener('skymp5-client:hideTradeInvite', hideInviteHandler);
    };
  }, []);

  const sendMove = (dir: 'add' | 'remove', item: UiItem, count: number): void => {
    send({ action: dir, baseId: item.baseId, count, extras: item.extras });
  };

  // Single items move whole; any real stack asks "how many?" first (like vanilla).
  const clickItem = (dir: 'add' | 'remove', item: UiItem): void => {
    if (item.count > STACK_PROMPT_THRESHOLD) {
      setPromptCount(1);
      setPrompt({ dir, item });
    } else {
      sendMove(dir, item, item.count);
    }
  };

  const confirmPrompt = (): void => {
    if (!prompt) return;
    const n = Math.max(1, Math.min(promptCount, prompt.item.count));
    sendMove(prompt.dir, prompt.item, n);
    setPrompt(null);
  };

  const clampPromptCount = (value: number): void => {
    if (!prompt) return;
    if (Number.isNaN(value)) {
      setPromptCount(1);
      return;
    }
    setPromptCount(Math.max(1, Math.min(Math.floor(value), prompt.item.count)));
  };

  if (inviteFrom !== null) {
    return (
      <div className="trade-overlay">
        <div className="trade-confirm">
          <div className="trade-confirm__title">Demande d'échange</div>
          <div className="trade-confirm__text">{inviteFrom} souhaite échanger avec toi.</div>
          <div className="trade-confirm__actions">
            <button className="trade-choose-btn" onClick={() => send({ action: 'inviteAccept' })}>Accepter</button>
            <button className="trade-choose-btn trade-choose-btn--ghost" onClick={() => send({ action: 'inviteDecline' })}>Refuser</button>
          </div>
        </div>
      </div>
    );
  }

  if (!state) return null;

  const tradeAvailable = state.bothLocked && !state.iAccepted;

  return (
    <div className="trade-overlay">
      <div className="trade-shell">
        <div className="trade-header">
          <span className="trade-header__brand">Échange</span>
          <h2 className="trade-header__title">avec {state.partnerName}</h2>
        </div>

        <div className="trade-body">
          <div className="trade-pane">
            <div className="trade-heading">Ton inventaire</div>
            <ItemList
              items={state.inventory}
              emptyText="Rien à échanger"
              onItemClick={(item) => clickItem('add', item)}
            />
          </div>

          <div className="trade-divider" />

          <div className="trade-actions">
            <button className="trade-choose-btn trade-choose-btn--danger" onClick={() => send({ action: 'cancel' })}>
              Annuler
            </button>
            <button
              className="trade-choose-btn trade-choose-btn--ghost"
              onClick={() => send({ action: state.myLocked ? 'unlock' : 'lock' })}
            >
              {state.myLocked ? 'Déverrouiller' : 'Verrouiller'}
            </button>
            <button
              className="trade-choose-btn"
              disabled={!tradeAvailable}
              onClick={() => send({ action: 'accept' })}
            >
              {state.iAccepted ? 'En attente…' : 'Échanger'}
            </button>
          </div>

          <div className="trade-divider" />

          <div className="trade-pane-group">
            <div className={'trade-pane' + (state.myLocked ? ' trade-pane--locked' : '')}>
              <div className="trade-heading">
                Ton offre {state.myLocked ? <span className="trade-lock-tag">verrouillée</span> : null}
              </div>
              <ItemList
                items={state.myOffer}
                emptyText="(vide)"
                onItemClick={state.myLocked ? undefined : (item) => clickItem('remove', item)}
              />
            </div>

            <div className={'trade-pane' + (state.theirLocked ? ' trade-pane--locked' : '')}>
              <div className="trade-heading">
                Offre de {state.partnerName}{' '}
                {state.theirLocked ? <span className="trade-lock-tag">verrouillée</span> : null}
                {state.theyAccepted ? <span className="trade-lock-tag trade-lock-tag--accent">en cours</span> : null}
              </div>
              <ItemList items={state.theirOffer} emptyText="(vide)" />
            </div>
          </div>
        </div>

        {prompt ? (
          <div className="trade-confirm-overlay">
            <div className="trade-confirm">
              <div className="trade-confirm__title">
                {prompt.dir === 'add' ? 'Ajouter combien de' : 'Retirer combien de'} {prompt.item.name} ?
              </div>
              <div className="trade-confirm__row">
                <button className="trade-step-btn" onClick={() => clampPromptCount(promptCount - 1)}>−</button>
                <input
                  className="trade-count-input"
                  type="number"
                  min={1}
                  max={prompt.item.count}
                  value={promptCount}
                  onChange={(e) => clampPromptCount(parseInt(e.target.value, 10))}
                />
                <button className="trade-step-btn" onClick={() => clampPromptCount(promptCount + 1)}>+</button>
                <button className="trade-choose-btn trade-choose-btn--ghost" onClick={() => setPromptCount(prompt.item.count)}>Tout</button>
              </div>
              <div className="trade-confirm__actions">
                <button className="trade-choose-btn" onClick={confirmPrompt}>Confirmer</button>
                <button className="trade-choose-btn trade-choose-btn--ghost" onClick={() => setPrompt(null)}>Retour</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Trade;
